import React, { useState } from "react";
import { Grid, Box, useMediaQuery, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useScaleGestures } from "../hooks/useScaleGestures.js";
import { useInitialBatchSize } from "../hooks/useInitialBatchSize.js";
import { useFillViewport } from "../hooks/useFillViewport.js";
import useApi from "../hooks/useApi.js";
import MotionInView from "../components/MotionInView.js";
import InfiniteScroll from "react-infinite-scroll-component";
import LibraryHeader from "../features/track/Header.js";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useDebounce } from "../hooks/useDebounce.js";
import LibraryList from "../features/track/LibraryList.js";
import JournalList from "../features/track/JournalList.js";
import MonthHeading from "../features/track/MonthHeading.js";
import PosterTile from "../components/PosterTile.js";
import Loading from "../components/Loading.js";
import Offline from "../components/Offline.js";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { setLastTrackPath } from "../utils/lastTrackPath.js";
import { getGroupByMonth, setGroupByMonth } from "../utils/groupByMonth.js";
import EmptyLibrary from "../features/track/EmptyLibrary.js";
import { useAuth } from "../context/authContext.js";
import { useDetailNavigation } from "../hooks/useDetailNavigation.js";
import { useDocumentMeta } from "../hooks/useDocumentMeta.js";
import LibrarySummary from "../features/track/LibrarySummary.js";
import {
  getContentNoun,
  getContentTypeFromSlug,
  getContentTypeSlug,
  getTrackPath,
  getSortByOptions,
  getStatusFromSlug,
  getStatusOptions,
  getStatusSlug,
} from "../utils/utils.js";
// import BetaPill from "../components/BetaPill.js";
import { FILTER_CONFIG } from "../utils/utils.js";

/**
 * The month ("YYYY-MM") an item falls in under a date sort — its finish date;
 * or, in the viewer's own timezone as the server counts them, the moment it
 * was last edited (edit date) or logged (log date: an entry's creation,
 * or a status being set). Null for an undated entry.
 */
const getItemMonth = (item, sortBy) => {
  if (sortBy === "finish_date") return item.date?.slice(0, 7) ?? null;
  const moment = new Date(
    sortBy === "edited"
      ? item.updated_at
      : (item.created_at ?? item.status_timestamp),
  );
  return `${moment.getFullYear()}-${String(moment.getMonth() + 1).padStart(2, "0")}`;
};

const resolveDefaultSortBy = (preferencesSortBy, status) => {
  if (!preferencesSortBy) return "log_date";
  return preferencesSortBy[status] ?? preferencesSortBy.default ?? "log_date";
};

export default function Track() {
  const scaleGestures = useScaleGestures();
  // Visitors' poster tiles have PosterTile's long press (and its pop), so they
  // drop the tap press-in that would otherwise mute it — hover only.
  const hoverOnlyGestures = useScaleGestures({ tap: false });
  // The tab (status + content type) the tab-change effect last saw.
  // Compared rather than a "has mounted" flag: StrictMode re-runs effects on
  // mount, which would otherwise reset a deep link's sort in development.
  const lastTabRef = React.useRef(null);
  // Remembers the last sort the user explicitly chose (key + direction).
  // Status/content-type navigation carries only filters in the new URL (see
  // getCarriedSearch below), so this ref is the only place that survives a
  // tab switch to carry a sort choice forward.
  const lastSortRef = React.useRef(null);
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { toggleDrawer, drawerWidth } = useOutletContext();
  const {
    fetchStatuses,
    fetchJournal,
    fetchFilterOptions,
    fetchLibrarySummary,
    fetchUserProfile,
  } = useApi();
  const { openContent } = useDetailNavigation();
  // The URL carries a content-type slug (movies|tv|books, or none for All) and
  // a status slug (queued|started|stopped|finished); convert both to the
  // internal values (all|movie|tvshow|book / queued|started|abandoned|finished)
  // used everywhere downstream.
  const {
    username: targetUsername,
    contentType: contentTypeSlug,
    status: statusSlug,
  } = useParams();
  const contentType = contentTypeSlug
    ? getContentTypeFromSlug(contentTypeSlug)
    : "all";
  const status = getStatusFromSlug(statusSlug);
  const isOwn = user?.username === targetUsername;
  const isDemo = !user && targetUsername === "demo";

  // Remember where you are in your own library (tab, filters, sort, search
  // and view all live in the URL), so returning to Track resumes it. Track is
  // rendered by AppRoutes' page layer, so this is the page's location even
  // with a content modal open over it.
  const location = useLocation();
  React.useEffect(() => {
    if (isOwn) {
      setLastTrackPath(user.username, location.pathname + location.search);
    }
  }, [isOwn, user?.username, location.pathname, location.search]);

  // Reuses the same status/content-type wording the UI already shows elsewhere
  // (getStatusOptions' "Stopped" for abandoned, not "Abandoned") rather than
  // inventing separate copy just for the tab title.
  useDocumentMeta({
    title: contentType
      ? `@${targetUsername}'s ${getStatusOptions()[status]?.label.toLowerCase()} ${getContentNoun({ type: contentType, plural: true })} on Curate`
      : undefined,
  });

  // Dynamic filter options are only fetched once one of those menus is opened
  const [filterOptionsEnabled, setFilterOptionsEnabled] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSort = resolveDefaultSortBy(user?.preferences?.sortBy, status);
  const sortBy = searchParams.get("sortBy") ?? defaultSort;
  // Every sort has its own natural opening direction (title opens ascending,
  // everything else opens descending) — an absent "sortDesc" param means "use
  // whichever sort is active's own default", not an unconditional "true".
  const naturalDescendingFor = (sortByValue) =>
    getSortByOptions(status).find((option) => option.value === sortByValue)
      ?.defaultDescending ?? true;
  const sortDescending = searchParams.has("sortDesc")
    ? searchParams.get("sortDesc") !== "false"
    : naturalDescendingFor(sortBy);
  const searchTerm = searchParams.get("q") ?? "";
  // Grid or list: your saved preference, unless the URL says otherwise (only
  // written when it differs, like sort), so it survives leaving the page
  const defaultView = user?.preferences?.view ?? "grid";
  const view = searchParams.get("view") ?? defaultView;
  // Month grouping ("calendar view") for date sorts. One remembered choice
  // across every tab (see groupByMonth.js), off until turned on. A link can
  // set it with ?groupByMonth= (e.g. the dashboard's Recently finished row),
  // which wins on arrival and is then saved and stripped by the effect below.
  // See MonthHeading
  const [storedGroupByMonth, setStoredGroupByMonth] =
    useState(getGroupByMonth);
  const groupByMonthParam = searchParams.get("groupByMonth");
  const groupByMonth =
    groupByMonthParam !== null
      ? groupByMonthParam !== "false"
      : storedGroupByMonth;
  const showMonths =
    groupByMonth &&
    !!getSortByOptions(status).find((option) => option.value === sortBy)
      ?.groupsByMonth;
  const region = user?.preferences?.region || "GB";

  const filters = FILTER_CONFIG.map((config) => {
    const raw = searchParams.get(config.paramKey);
    if (!raw) return null;
    return { type: config.type, value: config.deserialize(raw) };
  }).filter(Boolean);

  const updateParam = (key, value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (
          value == null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          next.delete(key);
        } else {
          next.set(key, Array.isArray(value) ? value.join(",") : value);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Omits "sortDesc" when it matches the active sort's own natural direction
  // (keeps the URL clean), writes it explicitly only when the user has
  // deviated from that default.
  const setSortDescending = (val) =>
    updateParam(
      "sortDesc",
      val === naturalDescendingFor(sortBy) ? null : val ? "true" : "false",
    );
  const setSearchTerm = (val) => updateParam("q", val);

  // Sets sortBy and sortDescending together in a single setSearchParams call.
  // react-router's setSearchParams closes over the searchParams snapshot from
  // the current render rather than chaining like useState's functional
  // updater — two separate calls in the same handler (e.g. setSortBy then
  // setSortDescending) would each build off that same stale snapshot, so the
  // second call's navigate() silently discards the first's change.
  const setSortByAndDescending = (newSortBy, newSortDescending) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (newSortBy === defaultSort) {
          next.delete("sortBy");
        } else {
          next.set("sortBy", newSortBy);
        }

        if (newSortDescending === naturalDescendingFor(newSortBy)) {
          next.delete("sortDesc");
        } else {
          next.set("sortDesc", newSortDescending ? "true" : "false");
        }

        return next;
      },
      { replace: true },
    );
  };

  const setFilters = (newFiltersOrFn) => {
    const newFilters =
      typeof newFiltersOrFn === "function"
        ? newFiltersOrFn(filters)
        : newFiltersOrFn;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Clear all filter params
        FILTER_CONFIG.forEach((config) => next.delete(config.paramKey));
        // Re-apply from new filters array
        newFilters.forEach((filter) => {
          const config = FILTER_CONFIG.find((c) => c.type === filter.type);
          if (config) next.set(config.paramKey, config.serialize(filter.value));
        });
        return next;
      },
      { replace: true },
    );
  };
  const saveGroupByMonth = (value) => {
    setStoredGroupByMonth(value);
    setGroupByMonth(value);
  };

  // A link's ?groupByMonth= becomes the remembered choice, then leaves the
  // URL so it can't override a later toggle (e.g. when lastTrackPath restores
  // this URL). Both updates land in one render, so there's no flicker.
  React.useEffect(() => {
    if (groupByMonthParam === null) return;
    saveGroupByMonth(groupByMonthParam !== "false");
    updateParam("groupByMonth", null);
    // saveGroupByMonth and updateParam only wrap stable setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByMonthParam]);

  const handleGroupByMonthToggle = () => saveGroupByMonth(!groupByMonth);

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      updateParam("view", newView === defaultView ? null : newView);
    }
  };

  const handleSortByChange = (event, newSortBy) => {
    if (newSortBy === null) return;
    const newSortDescending = naturalDescendingFor(newSortBy);
    setSortByAndDescending(newSortBy, newSortDescending);
    lastSortRef.current = {
      sortBy: newSortBy,
      sortDescending: newSortDescending,
    };
  };

  const toggleSortDescending = () => {
    const newSortDescending = !sortDescending;
    setSortDescending(newSortDescending);
    lastSortRef.current = { sortBy, sortDescending: newSortDescending };
  };

  // Filters, the title search and the view carry over to the tab being
  // switched to, except those that tab can't use — per each filter's own
  // `disabled` rule, e.g. rating off the finished tab, or genre on All. Sort
  // is carried separately, by the tab-change effect below, and month grouping
  // needs no carrying, as it's remembered outside the URL.
  const getCarriedSearch = (newContentType, newStatus) => {
    const carried = new URLSearchParams();
    FILTER_CONFIG.forEach((config) => {
      const value = searchParams.get(config.paramKey);
      if (value && !config.disabled(newStatus, newContentType)) {
        carried.set(config.paramKey, value);
      }
    });
    if (searchTerm) carried.set("q", searchTerm);
    if (searchParams.has("view")) carried.set("view", view);
    const query = carried.toString();
    return query ? `?${query}` : "";
  };

  const handleContentChange = (newContentType) => {
    navigate(
      getTrackPath(targetUsername, newContentType, status) +
        getCarriedSearch(newContentType, status),
    );
  };

  const handleStatusChange = (newStatus) => {
    navigate(
      getTrackPath(targetUsername, contentType, newStatus) +
        getCarriedSearch(contentType, newStatus),
    );
  };

  const handleFiltersChange = (newValue) => {
    setFilters(newValue);
  };

  // Removes every filter and the title search in one URL update (two
  // separate setSearchParams calls in one handler would clobber each other)
  const handleClearAll = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        FILTER_CONFIG.forEach((config) => next.delete(config.paramKey));
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  };

  const handleSearchTermChange = (newValue) => {
    setSearchTerm(newValue);
  };

  const statusesInfiniteQuery = useInfiniteQuery({
    queryKey: [
      "infStatuses",
      targetUsername,
      contentType,
      status,
      { sortBy, sortDescending, filters, searchTerm, showMonths },
    ],
    queryFn: ({ pageParam = 1 }) =>
      fetchStatuses({
        pageParam,
        perPage: 24,
        username: targetUsername,
        contentType,
        status,
        sortBy,
        sortDescending,
        filters,
        searchTerm,
        region,
        groupByMonth: showMonths,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: status !== "finished",
  });

  const statusesList = React.useMemo(
    () => statusesInfiniteQuery.data?.pages.flatMap((page) => page.items) || [],
    [statusesInfiniteQuery.data],
  );

  // Only the batch loaded on first render of the current filter/sort context
  // gets an entrance animation — cards appended afterward via infinite scroll
  // (or the current batch, once already settled) should never animate again.
  const statusesGenerationKey = JSON.stringify([
    targetUsername,
    contentType,
    status,
    sortBy,
    sortDescending,
    filters,
    searchTerm,
  ]);
  const statusesInitialBatchSize = useInitialBatchSize(
    statusesList,
    statusesGenerationKey,
  );

  const journalInfiniteQuery = useInfiniteQuery({
    queryKey: [
      "infJournal",
      targetUsername,
      contentType,
      status,
      { sortBy, sortDescending, filters, searchTerm, showMonths },
    ],
    queryFn: ({ pageParam = 1 }) =>
      fetchJournal({
        pageParam,
        perPage: 24,
        username: targetUsername,
        contentType,
        sortDescending,
        sortBy,
        filters,
        searchTerm,
        region,
        groupByMonth: showMonths,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: status === "finished",
  });

  // Posters per row in grid view (from the item sizes below), so a month's
  // header strip knows when it continues a row from the poster to its left
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const gridColumns = isMdUp ? 6 : isSmUp ? 4 : 3;

  const journalList = React.useMemo(
    () => journalInfiniteQuery.data?.pages.flatMap((page) => page.items) || [],
    [journalInfiniteQuery.data],
  );

  // Each month's count by type, for the month headings — from the server,
  // which counts the whole filtered list, so a month split across pages is
  // complete either side. Merged across the pages loaded so far.
  const activeListData =
    status === "finished"
      ? journalInfiniteQuery.data
      : statusesInfiniteQuery.data;
  const monthCounts = React.useMemo(
    () =>
      Object.assign(
        {},
        ...(activeListData?.pages.map((page) => page.monthCounts) ?? []),
      ),
    [activeListData],
  );

  /**
   * Month markers for the item at `index` in `list`, when grouping by month:
   * in list view a heading row opening each month (`heading`, placed before
   * the item); in grid view a header strip on every poster (`strip`, placed
   * inside its grid item), keeping the grid intact. See MonthHeading.
   */
  const getMonthMarkers = (list, index) => {
    if (!showMonths) return {};
    const month = getItemMonth(list[index], sortBy);
    const startsMonth =
      index === 0 || month !== getItemMonth(list[index - 1], sortBy);
    const counts = monthCounts[month ?? "none"];

    if (view === "list") {
      return {
        heading: startsMonth && (
          <Grid item xs={12} key={`month-${month ?? "none"}`}>
            <MonthHeading
              month={month}
              variant="row"
              isFirst={index === 0}
              counts={counts}
            />
          </Grid>
        ),
      };
    }
    // On All, a label counting more than one type is wider than a poster at
    // any width (on a phone, or on desktop with the drawer taking its share),
    // so one starting in the last column would run off the page: it shows only
    // the month, and its counts move to the month's next poster, which must
    // exist (more than one type means more than one entry) and starts the row
    // below. A single icon and count fits a poster, so stays put.
    const defersCounts = (i) =>
      i % gridColumns === gridColumns - 1 &&
      Object.keys(monthCounts[getItemMonth(list[i], sortBy) ?? "none"] ?? {})
        .length > 1;
    let countsPlacement = "label";
    if (startsMonth && defersCounts(index)) {
      countsPlacement = "deferred";
    } else if (
      !startsMonth &&
      index % gridColumns === 0 &&
      defersCounts(index - 1) &&
      (index === 1 || getItemMonth(list[index - 2], sortBy) !== month)
    ) {
      countsPlacement = "carried";
    }

    return {
      strip: (
        <MonthHeading
          month={month}
          variant="strip"
          startsMonth={startsMonth}
          counts={counts}
          countsPlacement={countsPlacement}
          // Same month, and not the first poster of a row
          bridgesLeft={!startsMonth && index % gridColumns !== 0}
        />
      ),
    };
  };

  // Same entrance-animation gating as statusesList, above.
  const journalGenerationKey = JSON.stringify([
    targetUsername,
    contentType,
    status,
    sortBy,
    sortDescending,
    filters,
    searchTerm,
  ]);
  const journalInitialBatchSize = useInitialBatchSize(
    journalList,
    journalGenerationKey,
  );

  React.useEffect(() => {
    const tab = `${contentType}/${status}`;
    const isFirstRun = lastTabRef.current === null;
    const tabChanged = lastTabRef.current !== tab;
    lastTabRef.current = tab;
    if (isFirstRun || !tabChanged) return; // Keep the URL's own sort on load
    setSearchParams(
      (prev) => {
        // Filters arrive already carried over (see getCarriedSearch); only
        // the sort is restored here.
        const next = new URLSearchParams(prev);

        // The navigation above drops sort params, so the previous sort
        // choice can only be recovered from lastSortRef — reuse it if that
        // sort is still selectable on the incoming status (title/year always
        // are, so they survive every tab switch; rating/finish_date only
        // survive a trip back to finished); otherwise fall back to the
        // resolved preference default.
        const remembered = lastSortRef.current;
        const rememberedStillEnabled =
          remembered &&
          getSortByOptions(status).find(
            (option) => option.value === remembered.sortBy && !option.disabled,
          );

        const newSortBy = rememberedStillEnabled
          ? remembered.sortBy
          : resolveDefaultSortBy(user?.preferences?.sortBy, status);
        // When there's nothing remembered to carry over, land on that sort's
        // own natural direction rather than an unconditional descending.
        const newSortDescending = rememberedStillEnabled
          ? remembered.sortDescending
          : naturalDescendingFor(newSortBy);

        if (newSortBy && newSortBy !== defaultSort) {
          next.set("sortBy", newSortBy);
        } else {
          next.delete("sortBy");
        }

        if (newSortDescending === naturalDescendingFor(newSortBy)) {
          next.delete("sortDesc");
        } else {
          next.set("sortDesc", newSortDescending ? "true" : "false");
        }

        return next;
      },
      { replace: true },
    );
    // setSearchParams is stable per React Router contract — safe to omit
    // user?.preferences?.sortBy is intentionally omitted — only status and
    // contentType changes should reset the sort
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, contentType]);

  // Text typed in the header search, which offers matching titles to pick
  // rather than filtering as you type. Debounced here, as each change is a
  // request; cleared at once so stale matches never linger.
  const [titleQuery, setTitleQuery] = useState("");
  const debouncedSetTitleQuery = useDebounce(setTitleQuery, 250);
  const handleTitleQueryChange = (query) => {
    if (query) {
      debouncedSetTitleQuery(query);
    } else {
      debouncedSetTitleQuery.cancel();
      setTitleQuery("");
    }
  };

  // Matching titles from the current tab, within the active filters: the
  // tab's own list endpoint with the query as its search term. Finished
  // entries can repeat a title (rewatches), so they're de-duplicated.
  const titleSuggestionsQuery = useQuery({
    queryKey: [
      "titleSuggestions",
      targetUsername,
      contentType,
      status,
      { filters, titleQuery },
    ],
    queryFn: async () => {
      const fetchList = status === "finished" ? fetchJournal : fetchStatuses;
      const page = await fetchList({
        pageParam: 1,
        perPage: 12,
        username: targetUsername,
        contentType,
        status,
        sortBy,
        sortDescending,
        filters,
        searchTerm: titleQuery,
        region,
      });
      const seen = new Set();
      return page.items
        .filter(
          (item) => !seen.has(item.content_id) && seen.add(item.content_id),
        )
        .slice(0, 5);
    },
    enabled: !!titleQuery,
    // Keeps the last matches showing while the next ones load, so the list
    // doesn't flicker on each keystroke
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  const filterOptionsQuery = useQuery({
    queryKey: ["filterOptions", targetUsername, contentType],
    queryFn: () =>
      fetchFilterOptions({ username: targetUsername, contentType }),
    // Also needed up front when a dynamic filter arrives in the URL, so its
    // chip can show names (e.g. a director) rather than just the filter type
    enabled:
      filterOptionsEnabled ||
      filters.some(
        (filter) => FILTER_CONFIG.find((c) => c.type === filter.type)?.dynamic,
      ),
    staleTime: 5 * 60 * 1000,
  });

  // Someone else's library names its owner in the summary strip; the avatar
  // shares Profile's cache, and a failed fetch just leaves the fallback avatar
  const showOwner = !isOwn && !isDemo;
  const ownerProfileQuery = useQuery({
    queryKey: ["profile", targetUsername],
    queryFn: () => fetchUserProfile(targetUsername),
    enabled: showOwner,
    staleTime: 10 * 60 * 1000,
    throwOnError: false,
  });

  // The summary strip's counts and stat for this tab as filtered. Sort and
  // view don't change what's counted, so aren't part of the key.
  const summaryQuery = useQuery({
    queryKey: [
      "librarySummary",
      targetUsername,
      contentType,
      status,
      { filters, searchTerm },
    ],
    queryFn: () =>
      fetchLibrarySummary({
        username: targetUsername,
        contentType,
        status,
        filters,
        searchTerm,
        region,
      }),
    // Holds the last numbers while a filter change refetches, so they change
    // in place — but only within a tab, as another tab's stat would mislead
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === contentType &&
      previousQuery?.queryKey[3] === status
        ? previousData
        : undefined,
  });

  // The stats page gets this view's tab, filters and search, ready for stats
  // scoped to them
  const statsSearch = new URLSearchParams({ status: getStatusSlug(status) });
  if (contentType !== "all") {
    statsSearch.set("type", getContentTypeSlug(contentType));
  }
  FILTER_CONFIG.forEach((config) => {
    const value = searchParams.get(config.paramKey);
    if (value) statsSearch.set(config.paramKey, value);
  });
  if (searchTerm) statsSearch.set("q", searchTerm);

  // Only one of the two lists is on screen at a time, so only that one fills.
  useFillViewport(journalInfiniteQuery, status === "finished");
  useFillViewport(statusesInfiniteQuery, status !== "finished");

  // const booksBetaMessage = (
  //   <Typography
  //     component="div"
  //     sx={{
  //       display: "flex",
  //       alignItems: "center",
  //       gap: 1, // 👈 controls spacing
  //       flex: 1,
  //       justifyContent: {
  //         xs: "flex-start",
  //         sm: "center",
  //       },
  //       fontSize: {
  //         xs: "0.875rem",
  //         sm: "1rem",
  //       },
  //       color: "text.disabled",
  //     }}
  //   >
  //     <BetaPill />
  //     Help improve Curate by flagging incorrect or low-quality book covers
  //   </Typography>
  // );

  return (
    <Box width="100%" sx={{ pb: 2 }}>
      <LibraryHeader
        content={contentType}
        onContentChange={handleContentChange}
        status={status}
        onStatusChange={handleStatusChange}
        view={view}
        onViewChange={handleViewChange}
        sortBy={sortBy}
        onSortByChange={handleSortByChange}
        sortDescending={sortDescending}
        toggleSortDescending={toggleSortDescending}
        groupByMonth={groupByMonth}
        onGroupByMonthToggle={handleGroupByMonthToggle}
        toggleDrawer={toggleDrawer}
        activeFilters={filters}
        onActiveFiltersChange={handleFiltersChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        drawerWidth={drawerWidth}
        onDynamicFilterOpen={() => setFilterOptionsEnabled(true)}
        filterOptions={filterOptionsQuery.data ?? {}}
        filterOptionsLoading={filterOptionsQuery.isLoading}
        onTitleQueryChange={handleTitleQueryChange}
        titleSuggestions={titleQuery ? (titleSuggestionsQuery.data ?? []) : []}
        onClearAll={handleClearAll}
      />

      {/* {contentType === "book" && <LibraryBanner content={booksBetaMessage} />} */}

      <LibrarySummary
        summary={summaryQuery.data}
        contentType={contentType}
        statsPath={isOwn ? `/stats/u/${targetUsername}?${statsSearch}` : null}
        owner={
          showOwner
            ? {
                username: targetUsername,
                avatarUrl: ownerProfileQuery.data?.avatar_url,
              }
            : undefined
        }
        isDemo={isDemo}
        onExit={
          !isOwn && user
            ? () => navigate(`/track/u/${user.username}`)
            : undefined
        }
        viewerAvatarUrl={user?.avatar_url}
        onSignUp={isDemo ? login : undefined}
      />

      {status === "finished" && (
        <>
          {/* isError here is only ever a network error — server errors throw and are caught by QueryErrorBoundary */}
          {journalInfiniteQuery.isError && <Offline />}

          {journalInfiniteQuery.isLoading && (
            <Box
              width="100%"
              height="75dvh"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Loading />
            </Box>
          )}

          {journalInfiniteQuery.isSuccess && journalList.length === 0 ? (
            <EmptyLibrary
              filtersApplied={filters.length !== 0 || searchTerm !== ""}
              status={status}
              contentType={contentType}
              targetUsername={targetUsername}
              isOwn={isOwn}
            />
          ) : (
            <InfiniteScroll
              dataLength={journalList.length}
              next={journalInfiniteQuery.fetchNextPage}
              hasMore={!!journalInfiniteQuery.hasNextPage}
              loader={
                <Box
                  sx={{
                    pt: 2,
                    pb: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Loading inline />
                </Box>
              }
              style={{ overflow: "visible" }}
              // Overflow visbile required to ensure framer-motion scale animation applied to library cards displays properly
            >
              <Grid
                container
                columnSpacing={view === "list" ? 1 : 2}
                // Grid rows sit a touch closer when each carries a month strip,
                // which adds its own space above the posters
                rowSpacing={view === "list" ? 1 : showMonths ? 1.5 : 2}
              >
                {/* Every tile passes its entry, own track page or not —
                    LibraryModal decides from ownership whether to scope the
                    You tab to it or give it an @username tab. The demo library
                    is the exception: it's shown to signed-out visitors as if
                    it were their own, so it opens on You's demo journal
                    instead of an @demo tab. */}
                {journalList.map((entry, index) => {
                  const { heading, strip } = getMonthMarkers(
                    journalList,
                    index,
                  );

                  const card = (
                    <motion.div
                      layout="position"
                      {...(view !== "list" && !isOwn
                        ? hoverOnlyGestures
                        : scaleGestures)}
                    >
                      {view === "list" ? (
                        <JournalList
                          item={entry}
                          // All mixes types, so mark each row's type
                          showType={contentType === "all"}
                          // Grouped by finish date, the month heading gives the
                          // year (other date sorts' months can differ from it)
                          hideYear={showMonths && sortBy === "finish_date"}
                          onClick={() =>
                            openContent({
                              contentId: entry.content_id,
                              contentType: entry.content_type,
                              title: entry.content_title,
                              entryId: isDemo ? null : entry.journal_id,
                            })
                          }
                        />
                      ) : (
                        <PosterTile
                          contentId={entry.content_id}
                          contentType={entry.content_type}
                          contentTitle={entry.content_title}
                          imageUrl={entry.image_url}
                          showStatus={!isOwn ? "full" : null}
                          // All mixes types, so mark each poster's type
                          showType={contentType === "all"}
                          onClick={() =>
                            openContent({
                              contentId: entry.content_id,
                              contentType: entry.content_type,
                              title: entry.content_title,
                              entryId: isDemo ? null : entry.journal_id,
                            })
                          }
                        />
                      )}
                    </motion.div>
                  );
                  return [
                    heading,
                    <Grid
                      item
                      key={`journal-${entry.journal_id}`}
                      xs={view === "list" ? 12 : 4}
                      sm={view === "list" ? 12 : 3}
                      md={view === "list" ? 12 : 2}
                      lg={view === "list" ? 12 : 2}
                      xl={view === "list" ? 12 : 2}
                    >
                      {strip}
                      {index < journalInitialBatchSize ? (
                        <MotionInView inView delay={Math.min(index, 6) * 0.04}>
                          {card}
                        </MotionInView>
                      ) : (
                        card
                      )}
                    </Grid>,
                  ];
                })}
              </Grid>
            </InfiniteScroll>
          )}
        </>
      )}

      {status !== "finished" && (
        <>
          {/* isError here is only ever a network error — server errors throw and are caught by QueryErrorBoundary */}
          {statusesInfiniteQuery.isError && <Offline />}

          {statusesInfiniteQuery.isLoading && (
            <Box
              width="100%"
              height="75dvh"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Loading />
            </Box>
          )}

          {statusesInfiniteQuery.isSuccess &&
          status !== "finished" &&
          statusesList.length === 0 ? (
            <EmptyLibrary
              filtersApplied={filters.length !== 0 || searchTerm !== ""}
              status={status}
              contentType={contentType}
              targetUsername={targetUsername}
              isOwn={isOwn}
            />
          ) : (
            <InfiniteScroll
              dataLength={statusesList.length}
              next={statusesInfiniteQuery.fetchNextPage}
              hasMore={!!statusesInfiniteQuery.hasNextPage}
              loader={
                <Box
                  sx={{
                    pt: 2,
                    pb: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Loading inline />
                </Box>
              }
              // scrollableTarget="scrollableDiv"
              style={{ overflow: "visible" }} // Required to ensure framer-motion scale animation applied to library cards displays properly
            >
              <Grid
                container
                columnSpacing={view === "list" ? 1 : 2}
                // As the finished list: closer rows when each has month strips
                rowSpacing={view === "list" ? 1 : showMonths ? 1.5 : 2}
              >
                {statusesList.map((item, index) => {
                  const { heading, strip } = getMonthMarkers(
                    statusesList,
                    index,
                  );
                  const card = (
                    <motion.div
                      layout="position"
                      {...(view !== "list" && !isOwn
                        ? hoverOnlyGestures
                        : scaleGestures)}
                    >
                      {view === "list" ? (
                        <LibraryList
                          item={item}
                          showType={contentType === "all"}
                          onClick={() =>
                            openContent({
                              contentId: item.content_id,
                              contentType: item.content_type,
                              title: item.content_title,
                              // Same rule as the finished tiles above; null
                              // for the rare status with no matching entry.
                              entryId: isDemo ? null : item.journal_id,
                            })
                          }
                        />
                      ) : (
                        <PosterTile
                          contentId={item.content_id}
                          contentType={item.content_type}
                          contentTitle={item.content_title}
                          showStatus={!isOwn ? "full" : null}
                          imageUrl={item.image_url}
                          showType={contentType === "all"}
                          onClick={() =>
                            openContent({
                              contentId: item.content_id,
                              contentType: item.content_type,
                              title: item.content_title,
                              // Same rule as the finished tiles above; null
                              // for the rare status with no matching entry.
                              entryId: isDemo ? null : item.journal_id,
                            })
                          }
                        />
                      )}
                    </motion.div>
                  );
                  return [
                    heading,
                    <Grid
                      item
                      key={`status-${item.content_id}`}
                      xs={view === "list" ? 12 : 4}
                      sm={view === "list" ? 12 : 3}
                      md={view === "list" ? 12 : 2}
                      lg={view === "list" ? 12 : 2}
                      xl={view === "list" ? 12 : 2}
                    >
                      {strip}
                      {index < statusesInitialBatchSize ? (
                        <MotionInView inView delay={Math.min(index, 6) * 0.04}>
                          {card}
                        </MotionInView>
                      ) : (
                        card
                      )}
                    </Grid>,
                  ];
                })}
              </Grid>
            </InfiniteScroll>
          )}
        </>
      )}
    </Box>
  );
}
